import { Button, Space, Table, Tag } from "antd";
import { CALL_API } from "common/API";
import { STATUS_CODE } from "common/Constants";
import dayjs from "dayjs";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { getClientid, getLocations, getUserInfo } from "redux/selectors";
import styled from "styled-components";
import { isDateBetweenTwoDates } from "utils/Date";
import { UpOutlined, DownOutlined } from "@ant-design/icons";

const PreviewCalendarWrap = styled.div`
  .ant-table-wrapper {
    overflow: hidden;
    max-width: 340px;
  }
  .step2-right {
    padding: 0px !important;
  }
  .custom-table-row > td {
    height: 50px;
    // padding: 10px !important;
    vertical-align: top;
  }
  .ant-space {
    padding: 0px 10px !important;
  }

  .ant-table-tbody > tr > td {
    padding: 0px;
  }

  small {
    color: #636980;
    font-size: 10px;
  }

  .past_columns {
    background-color: #fafafa !important;
  }
`;

const PreviewShift = ({ userList = [], assignedShiftList = [] }) => {
  const userInfo = useSelector(getUserInfo);
  const ClientId = useSelector(getClientid);
  const locationList = useSelector(getLocations);

  const filterUser = useMemo(
    () => userList.filter((item) => item?.userid === userInfo?.userid),
    [userList, userInfo?.userid]
  );

  const userTimeZone =
    locationList?.find(
      (val) => val?.location_id === filterUser?.[0]?.baselocationid
    )?.usertimezone || "Africa/Nairobi";

  const [currentDate, setCurrentDate] = useState(dayjs());
  const [leaveList, setLeaveList] = useState([]);
  const [holidayList, setHolidayList] = useState([]);
  const [shiftList, setShiftList] = useState([]);

  const today = dayjs().startOf("day");

  const getHolidayList = useCallback(async () => {
    try {
      const { data } = await CALL_API(`holiday-list/${ClientId}`, "post", {
        start_date: currentDate.startOf("month").format("YYYY-MM-DD"),
        end_date: currentDate.endOf("month").format("YYYY-MM-DD"),
      });

      setHolidayList(Array.isArray(data) ? data : []);
    } catch (err) {
      console.log("Holiday API error", err);
      setHolidayList([]);
    }
  }, [ClientId, currentDate]);

  const getLeaveList = useCallback(async () => {
    try {
      const { code, leaves } = await CALL_API(
        `leave-list/${ClientId}`,
        "post",
        {
          status_id: "4,5",
          department_id: [""],
          selected_role_id: "",
          start_date: currentDate.startOf("month").format("YYYY-MM-DD"),
          end_date: currentDate.endOf("month").format("YYYY-MM-DD"),
          show_my_team_leave: false,
        }
      );

      if (code === STATUS_CODE.SUCCESS) {
        setLeaveList(Array.isArray(leaves) ? leaves : []);
      } else {
        setLeaveList([]);
      }
    } catch (err) {
      console.log("Leave API error", err);
      setLeaveList([]);
    }
  }, [ClientId, currentDate]);

  const getShiftList = useCallback(async () => {
    try {
      const { data } = await CALL_API(`get-shift/${ClientId}`, "get", {});
      setShiftList(Array.isArray(data) ? data : []);
    } catch (error) {
      console.log("Shift API error", error);
      setShiftList([]);
    }
  }, [ClientId]);

  const days = useMemo(() => {
    const start = currentDate.startOf("month");
    const end = currentDate.endOf("month");
    const arr = [];

    for (
      let d = start;
      d.isBefore(end) || d.isSame(end, "day");
      d = d.add(1, "day")
    ) {
      arr.push(d);
    }

    return arr;
  }, [currentDate]);

  const assignedShiftMap = useMemo(() => {
    const map = {};

    assignedShiftList.forEach((item) => {
      if (!item?.userId) return;

      if (!map[item.userId]) {
        map[item.userId] = {};
      }

      (item.dates || []).forEach((d) => {
        map[item.userId][d.date] = d;
      });
    });

    return map;
  }, [assignedShiftList]);

  const leaveMap = useMemo(() => {
    const map = {};

    leaveList.forEach((l) => {
      if (!l?.userid) return;
      if (!map[l.userid]) map[l.userid] = [];
      map[l.userid].push(l);
    });

    return map;
  }, [leaveList]);

  const shiftMap = useMemo(() => {
    const map = {};
    shiftList.forEach((s) => {
      if (s?.shift_id != null) {
        map[s.shift_id] = s;
      }
    });
    return map;
  }, [shiftList]);

  const dataSource = useMemo(() => {
    return (userList || []).map((user) => {
      const emp = {
        key: user.userid,
        fullname: user.fullname,
        employeeid: user.employeeid,
        userid: user.userid,
      };

      const userShiftDates = assignedShiftMap[user.userid] || {};
      const userLeaves = leaveMap[user.userid] || [];

      days.forEach((d) => {
        const dateStr = d.format("YYYY-MM-DD");

        const shift = userShiftDates[dateStr];

        const leave = userLeaves.find((l) => {
          const startDate = l.new_start_date || l.start_date;
          const endDate = l.new_end_date || l.end_date;
          return dateStr >= startDate && dateStr <= endDate;
        });

        emp[dateStr] = shift?.shiftcode || null;
        emp[`${dateStr}_shift`] = shift || null;
        emp[`${dateStr}_leave`] = leave || null;
      });

      return emp;
    });
  }, [userList, days, assignedShiftMap, leaveMap]);

  const handlePrevMonth = () => {
    setCurrentDate((prev) => prev.subtract(1, "month"));
  };

  const handleNextMonth = () => {
    setCurrentDate((prev) => prev.add(1, "month"));
  };

  const renderCell = useCallback(
    (value, record, date) => {
      const colKey = date.format("YYYY-MM-DD");

      const shift = record[`${colKey}_shift`];
      const leave = record[`${colKey}_leave`];

      const baselocationid = userList?.find(
        (u) => u.userid === record.userid
      )?.baselocationid;

      const isHoliday = holidayList?.some(
        (h) =>
          h.location_id?.includes(baselocationid) &&
          isDateBetweenTwoDates(colKey, h.start_date, h.end_date)
      );

      const shiftDetails = shiftMap[shift?.shift_id];
      const daynumber = date.day();

      const isWeekend =
        (shiftDetails?.isweekend === "allsaturdayandsunday" &&
          (daynumber === 0 || daynumber === 6)) ||
        (shiftDetails?.isweekend === "allsaturdays" && daynumber === 6) ||
        (shiftDetails?.isweekend === "allsundays" && daynumber === 0);

      /* ------------------ UI COLORS ------------------ */

      let bgColor = "#fff";
      let text = "";

      if (leave) {
        bgColor = leave.status_id === "5" ? "#D4F5DD" : "#FFE7BA";
        text = leave.leave_category_name;
      } else if (isHoliday) {
        bgColor = "#EDEDED";
        text = "Hdy";
      } else if (isWeekend) {
        bgColor = "#EDEDED";
        text = "WE";
      } else if (shift) {
        bgColor = shift.color || "#f0f0f0";
        text = shift.shiftcode;
      }

      return (
        <div
          style={{
            height: "100%",
            width: "100%",
            background: bgColor,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 13,
            fontWeight: 500,
          }}
        >
          {text}
        </div>
      );
    },
    [holidayList, shiftMap, userList]
  );

  const columns = useMemo(() => {
    return [
      {
        title: (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <span>{currentDate.format("MMMM YYYY")}</span>

            <Space direction="vertical" size={0}>
              <Button
                size="small"
                type="text"
                icon={<UpOutlined />}
                onClick={handlePrevMonth}
              />
              <Button
                size="small"
                type="text"
                icon={<DownOutlined />}
                onClick={handleNextMonth}
              />
            </Space>
          </div>
        ),
        dataIndex: "fullname",
        fixed: "left",
        width: 180,
        render: (_, record) => (
          <Space direction="vertical" size={0}>
            <b style={{ fontWeight: 400 }}>{record.fullname}</b>
            <small>{record.employeeid}</small>
          </Space>
        ),
      },
      ...days.map((date) => ({
        title: (
          <div style={{ textAlign: "center" }}>
            <div>{date.date()}</div>
            <div style={{ fontSize: 12, color: "#636980" }}>
              {date.format("ddd")}
            </div>
          </div>
        ),
        dataIndex: date.format("YYYY-MM-DD"),
        width: 80,
        align: "center",
        className: date.isBefore(today, "day") ? "past_columns" : "",
        render: (value, record) => renderCell(value, record, date),
      })),
    ];
  }, [currentDate, days, renderCell, today]);

  useEffect(() => {
    getShiftList();
  }, [getShiftList]);

  useEffect(() => {
    getLeaveList();
    getHolidayList();
  }, [getLeaveList, getHolidayList]);

  return (
    <PreviewCalendarWrap>
      <Table
        dataSource={dataSource}
        columns={columns}
        pagination={false}
        rowClassName={() => "custom-table-row"}
        scroll={{ x: 300, y: 400 }}
        bordered
      />
    </PreviewCalendarWrap>
  );
};

export default PreviewShift;
